import logo from '@/assets/images/logo.png';
import { useRBACContext } from '@/domain/context/rbac';
import { ActionType, ResourceType } from '@/domain/types/rbac';
import React, { useState } from 'react';
import { IconType } from 'react-icons';
import {
  MdAnnouncement,
  MdBarChart,
  MdCampaign,
  MdChevronLeft,
  MdChevronRight,
  MdDashboard,
  MdExpandLess,
  MdExpandMore,
  MdFilterList,
  MdGroup,
  MdHistory,
  MdLink,
  MdManageAccounts,
  MdNotifications,
  MdPeople,
  MdPerson,
  MdPersonOff,
  MdPhotoLibrary,
  MdPodcasts,
  MdPolicy,
  MdReport,
  MdSecurity,
  MdSettings,
  MdStarRate,
  MdVideoLibrary,
  MdViewCarousel,
} from 'react-icons/md';
import { NavLink } from 'react-router-dom';
import { useSidebar } from './sidebar-context';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MenuItem {
  icon: IconType;
  label: string;
  path: string;
  resource?: ResourceType;
  children?: MenuItem[];
  isGroup?: boolean;
}

// ─── Menu definition ──────────────────────────────────────────────────────────

const menuItems: MenuItem[] = [
  { icon: MdDashboard, label: 'Dashboard', path: 'home' },
  {
    icon: MdNotifications,
    label: 'Comunicaciones',
    path: 'content-communications',
    isGroup: true,
    children: [
      { icon: MdAnnouncement, label: 'Última Hora', path: 'releases', resource: 'releases' },
      { icon: MdReport, label: 'Reportes', path: 'reports', resource: 'reports' },
      { icon: MdNotifications, label: 'Publicaciones', path: 'newsletters', resource: 'newsletters' },
      { icon: MdViewCarousel, label: 'Banners', path: 'banners', resource: 'banners' },
    ],
  },
  {
    icon: MdVideoLibrary,
    label: 'Multimedia',
    path: 'multimedia',
    isGroup: true,
    children: [
      { icon: MdVideoLibrary, label: 'Videos', path: 'videos', resource: 'videos' },
      { icon: MdPodcasts, label: 'Audios', path: 'podcasts', resource: 'podcasts' },
      { icon: MdPhotoLibrary, label: 'Galerías', path: 'galleries', resource: 'galleries' },
    ],
  },
  {
    icon: MdStarRate,
    label: 'Estándares',
    path: 'standards',
    isGroup: true,
    children: [
      { icon: MdStarRate, label: 'Estándares Críticos', path: 'critical-standars', resource: 'critical_standars' },
      { icon: MdPolicy, label: 'Políticas', path: 'policies', resource: 'policies' },
    ],
  },
  { icon: MdLink, label: 'Enlaces', path: 'links', resource: 'links' },
  { icon: MdCampaign, label: 'Campañas', path: 'campaigns', resource: 'campaigns' },
  {
    icon: MdPeople,
    label: 'Gestión de Usuarios',
    path: 'user-management',
    isGroup: true,
    children: [
      { icon: MdPeople, label: 'Usuarios', path: 'users', resource: 'users' },
      { icon: MdManageAccounts, label: 'Usuarios Admin', path: 'admin-users', resource: 'admin_users' },
      { icon: MdSecurity, label: 'Roles', path: 'roles', resource: 'roles' },
      { icon: MdGroup, label: 'Audiencias', path: 'audiences', resource: 'audiences' },
    ],
  },
  {
    icon: MdBarChart,
    label: 'Reportes y Análisis',
    path: 'reports-analytics',
    isGroup: true,
    children: [
      {
        icon: MdBarChart,
        label: 'Estadísticas',
        path: 'statistics',
        children: [
          { icon: MdFilterList, label: 'Reporte por filtros', path: 'statistics/report-by-filter' },
          { icon: MdPerson, label: 'Reporte por empleado', path: 'statistics/report-by-employee' },
          { icon: MdPeople, label: 'Usuarios que han ingresado', path: 'statistics/report-users-logged' },
        ],
      },
      { icon: MdHistory, label: 'Logs de Administradores', path: 'admin-logs', resource: 'admin_logs' },
    ],
  },
];

const settingsMenuItem: MenuItem = {
  icon: MdSettings,
  label: 'Configuración',
  path: 'settings',
  resource: 'settings',
};

const selfRegisteredMenuItem: MenuItem = {
  icon: MdPersonOff,
  label: 'Auto-registrados',
  resource: 'self_registered_users',
  path: 'users/self-registered',
};

// ─── RBAC filtering ───────────────────────────────────────────────────────────

function filterMenuItems(
  items: MenuItem[],
  hasPermission: (resource: ResourceType, action: ActionType) => boolean,
): MenuItem[] {
  return items.flatMap((item) => {
    if (item.isGroup && item.children) {
      const filtered = filterMenuItems(item.children, hasPermission);
      return filtered.length > 0 ? [{ ...item, children: filtered }] : [];
    }
    if (item.children) return [item]; // nested non-group (Estadísticas)
    if (!item.resource) return [item]; // no permission required (Dashboard)
    return hasPermission(item.resource, 'read') ? [item] : [];
  });
}

// ─── Shared tooltip wrapper for collapsed icons ───────────────────────────────

function Tooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="relative group">
      {children}
      <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity z-50 pointer-events-none">
        {label}
      </span>
    </div>
  );
}

// ─── Leaf nav item ────────────────────────────────────────────────────────────

function NavItem({ item, isCollapsed }: { item: MenuItem; isCollapsed: boolean }) {
  const link = (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
          isActive
            ? 'bg-primary text-white'
            : 'text-[#a0a4b0] hover:text-white hover:bg-white/10'
        } ${isCollapsed ? 'justify-center' : ''}`
      }
    >
      <item.icon size={20} className="flex-shrink-0" aria-hidden="true" />
      <span className={isCollapsed ? 'sr-only' : 'truncate'}>{item.label}</span>
    </NavLink>
  );

  return isCollapsed ? <Tooltip label={item.label}>{link}</Tooltip> : link;
}

// ─── Group item (collapsible) ─────────────────────────────────────────────────

function GroupItem({
  item,
  isCollapsed,
  hasPermission,
  onExpandSidebar,
}: {
  item: MenuItem;
  isCollapsed: boolean;
  hasPermission: (resource: ResourceType, action: ActionType) => boolean;
  onExpandSidebar: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // On mobile, this button sits inside the drawer container, which closes
    // the whole menu on click (so tapping a final nav link closes it, as
    // intended). Toggling a submenu isn't navigation, so it must not bubble
    // into that close handler — otherwise the menu closes before the
    // submenu it just expanded is ever seen.
    event.stopPropagation();
    if (isCollapsed) {
      onExpandSidebar();
      return;
    }
    setIsExpanded((prev) => !prev);
  };

  const button = (
    <button
      onClick={handleClick}
      aria-expanded={!isCollapsed ? isExpanded : undefined}
      aria-label={isCollapsed ? item.label : undefined}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[#a0a4b0] hover:text-white hover:bg-white/10 transition-colors ${
        isCollapsed ? 'justify-center' : 'justify-between'
      }`}
    >
      <div className="flex items-center gap-3">
        <item.icon size={20} className="flex-shrink-0" aria-hidden="true" />
        {!isCollapsed && <span className="truncate">{item.label}</span>}
      </div>
      {!isCollapsed && (
        <span className="flex-shrink-0">
          {isExpanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
        </span>
      )}
    </button>
  );

  return (
    <div>
      {isCollapsed ? <Tooltip label={item.label}>{button}</Tooltip> : button}
      {!isCollapsed && isExpanded && item.children && (
        <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
          {filterMenuItems(item.children, hasPermission).map((child) =>
            child.isGroup || child.children ? (
              <GroupItem
                key={child.path}
                item={child}
                isCollapsed={false}
                hasPermission={hasPermission}
                onExpandSidebar={onExpandSidebar}
              />
            ) : (
              <NavItem key={child.path} item={child} isCollapsed={false} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

// ─── Shared nav content ───────────────────────────────────────────────────────

function SidebarNav({
  isCollapsed,
  hasPermission,
  onExpandSidebar,
}: {
  isCollapsed: boolean;
  hasPermission: (resource: ResourceType, action: ActionType) => boolean;
  onExpandSidebar: () => void;
}) {
  const items = [...menuItems, settingsMenuItem, selfRegisteredMenuItem];
  const filtered = filterMenuItems(items, hasPermission);
  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 space-y-1">
      {filtered.map((item) =>
        item.isGroup || item.children ? (
          <GroupItem
            key={item.path}
            item={item}
            isCollapsed={isCollapsed}
            hasPermission={hasPermission}
            onExpandSidebar={onExpandSidebar}
          />
        ) : (
          <NavItem key={item.path} item={item} isCollapsed={isCollapsed} />
        ),
      )}
    </nav>
  );
}

// ─── AppSidebar ───────────────────────────────────────────────────────────────

const AppSidebar: React.FC = () => {
  const { isCollapsed, toggle, isMobileOpen, closeMobile } = useSidebar();
  const { hasPermission } = useRBACContext();

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className={`hidden md:flex flex-col flex-shrink-0 h-full bg-[#1a1d23] text-white overflow-hidden ${
          isCollapsed ? 'w-[60px]' : 'w-60'
        }`}
      >
        {/* Logo row */}
        <div
          className="flex items-center justify-center h-16 border-b border-white/10 px-3 flex-shrink-0"
        >
          <img
            src={logo}
            alt="Seguros Bolívar"
            className={isCollapsed ? 'h-8 w-auto object-contain' : 'h-11 w-auto max-w-[160px] object-contain'}
          />
        </div>

        {/* Nav items */}
        <SidebarNav
          isCollapsed={isCollapsed}
          hasPermission={hasPermission}
          onExpandSidebar={toggle}
        />

        {/* Collapse toggle */}
        <div className="border-t border-white/10 p-2 flex-shrink-0">
          <button
            onClick={toggle}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-[#a0a4b0] hover:text-white hover:bg-white/10 transition-colors text-sm ${
              isCollapsed ? 'justify-center' : ''
            }`}
            aria-label={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {isCollapsed ? (
              <MdChevronRight size={20} />
            ) : (
              <>
                <MdChevronLeft size={20} />
                <span>Colapsar</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mobile overlay drawer */}
      {isMobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={closeMobile}
            data-testid="mobile-backdrop"
          />
          <div
            className="fixed left-0 top-0 h-full w-60 bg-[#1a1d23] text-white z-50 md:hidden flex flex-col"
            onClick={closeMobile}
            data-testid="mobile-sidebar-drawer"
          >
            <div className="flex items-center h-16 border-b border-white/10 px-3 flex-shrink-0">
              <img src={logo} alt="Seguros Bolívar" className="w-24 object-contain" />
            </div>
            <SidebarNav
              isCollapsed={false}
              hasPermission={hasPermission}
              onExpandSidebar={() => {}}
            />
          </div>
        </>
      )}
    </>
  );
};

export default AppSidebar;
